import { useCallback } from 'react';

import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import * as customerApi from 'api/customerApi';
import {
  contractEditActions,
  contractEditSelectors,
} from 'store/reducers/customerReducer/contractEditSlice';
import {
  contractListActions,
  contractListSelectors,
} from 'store/reducers/customerReducer/contractListSlice';
import { listActions } from 'store/reducers/customerReducer/listSlice';
import { UpdateContract } from 'types/contract';
import { useToast } from 'hooks/useToast';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { ContractFormFields } from '../types/contractFormField';

/**
 * 顧客編集 store の実装をコンポーネントから隠すための中間層
 * @returns selector と action のラッパー
 */
export const useContractEditStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const isOpenInputModal = useAppSelector(
    contractEditSelectors.isOpenInputModal,
  );
  const isOpenConfirmationModal = useAppSelector(
    contractEditSelectors.isOpenConfirmationModal,
  );
  const isOpenDeleteModal = useAppSelector(
    contractEditSelectors.isOpenDeleteModal,
  );

  const menuId = useAppSelector(contractEditSelectors.menuId);
  const valid = useAppSelector(contractEditSelectors.valid);
  const startDate = useAppSelector(contractEditSelectors.startDate);
  const endDate = useAppSelector(contractEditSelectors.endDate);
  const displayValid = useAppSelector(contractEditSelectors.displayValid);
  const displayStartDate = useAppSelector(
    contractEditSelectors.displayStartDate,
  );
  const displayEndDate = useAppSelector(contractEditSelectors.displayEndDate);

  const selectedContract = useAppSelector(
    contractListSelectors.selectedContract,
  );
  const menuMap = useAppSelector(contractListSelectors.menuMap);
  const menuName = menuId ? menuMap[menuId].name ?? '' : '';

  const init = useCallback(() => {
    dispatch(contractEditActions.init());
  }, [dispatch]);

  const openModal = useCallback(() => {
    dispatch(contractEditActions.modalOpened());
  }, [dispatch]);

  const goInputModal = useCallback(() => {
    dispatch(contractEditActions.stepMovedToInput());
  }, [dispatch]);

  const goConfirmationModal = useCallback(() => {
    dispatch(contractEditActions.stepMovedToConfirmation());
  }, [dispatch]);

  const goDeleteModal = useCallback(() => {
    dispatch(contractEditActions.stepMovedToDelete());
  }, [dispatch]);

  const setForm = useCallback(
    (form: ContractFormFields) => {
      dispatch(
        contractEditActions.formGroupUpdated({
          form,
        }),
      );
    },
    [dispatch],
  );

  const changeStartDate = useCallback(
    (date: string) => {
      dispatch(
        contractEditActions.startDateChanged({
          startDate: date,
        }),
      );
    },
    [dispatch],
  );

  const changeEndDate = useCallback(
    (date: string) => {
      dispatch(
        contractEditActions.endDateChanged({
          endDate: date,
        }),
      );
    },
    [dispatch],
  );

  const putContractAndReload = useCallback(async () => {
    if (!menuId) {
      return;
    }
    if (!selectedContract) {
      return;
    }

    dispatch(loaderActions.setLoading(true));
    dispatch(listActions.loadStarted());
    const params: UpdateContract = {
      menuId,
      customerId: selectedContract.customerId,
      suspended: !valid,
      startDate,
      endDate,
    };
    const result = await customerApi.putContract(
      selectedContract.customerId,
      selectedContract.id,
      params,
    );
    dispatch(listActions.loadEnded());
    if (result.ok) {
      // 顧客一覧をリロードするために最終更新日を更新する
      dispatch(
        contractListActions.finalUpdateTimeUpdated({
          finalUpdateTime: new Date().toISOString(),
        }),
      );
      toast({
        status: 'success',
        description: '契約メニューの更新に成功しました。',
      });
    } else {
      toast({
        status: 'error',
        description: `契約メニューの更新に失敗しました: ${result.err.message}`,
      });
    }
    dispatch(loaderActions.setLoading(false));
  }, [menuId, selectedContract, dispatch, valid, startDate, endDate, toast]);

  const deleteContractAndReload = useCallback(async () => {
    if (!selectedContract) {
      return;
    }

    dispatch(loaderActions.setLoading(true));
    dispatch(listActions.loadStarted());

    const result = await customerApi.deleteContract(
      selectedContract.customerId,
      selectedContract.id,
    );
    dispatch(listActions.loadEnded());
    if (result.ok) {
      // 顧客一覧をリロードするために最終更新日を更新する
      dispatch(
        contractListActions.finalUpdateTimeUpdated({
          finalUpdateTime: new Date().toISOString(),
        }),
      );
      toast({
        status: 'success',
        description: '契約メニューの削除に成功しました。',
      });
    } else {
      toast({
        status: 'error',
        description: `契約メニューの削除に失敗しました: ${result.err.message}`,
      });
    }
    dispatch(loaderActions.setLoading(false));
  }, [selectedContract, dispatch, toast]);

  return {
    isOpenInputModal,
    isOpenConfirmationModal,
    isOpenDeleteModal,
    menuId,
    menuName,
    valid,
    startDate,
    endDate,
    displayValid,
    displayStartDate,
    displayEndDate,
    init,
    openModal,
    goInputModal,
    goConfirmationModal,
    goDeleteModal,
    setForm,
    changeStartDate,
    changeEndDate,
    putContractAndReload,
    deleteContractAndReload,
  };
};
